import { useState, useEffect } from 'react';
import { namedChainsApi, type NamedChainRow } from '@/features/admin/named-chains/api/named-chains-client';

interface Props {
  /** The currently selected named chain ID (empty string means none selected). */
  value: string;
  /** Called when the operator picks a chain from the dropdown. */
  onChange: (namedChainId: string) => void;
  /** Called when the operator clicks "Switch to inline". Receives the selected chain's steps for pre-population. */
  onSwitchToInline: (seedSteps: Array<{ type: string; config: Record<string, unknown> }>) => void;
  /** Optional: called when operator clicks "Promote to canonical" for a named-chain row. */
  onPromoteToCanonical?: () => void;
}

/**
 * NamedChainPicker — renders a named-chain dropdown + "Switch to inline" toggle button.
 * Owned by MappingPanel; rendered when a mapping's transforms.kind === 'named'.
 */
export function NamedChainPicker({ value, onChange, onSwitchToInline, onPromoteToCanonical }: Props) {
  const [chains, setChains] = useState<NamedChainRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    namedChainsApi
      .list()
      .then((res) => {
        if (!cancelled) setChains(res.chains);
      })
      .catch(() => {
        // silently ignore — empty dropdown is still usable
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  function handleSwitch() {
    const selectedChain = chains.find((c) => c.id === value);
    onSwitchToInline(selectedChain?.steps ?? []);
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <select
        aria-label="Named chain"
        value={value}
        disabled={loading}
        onChange={(e) => onChange(e.target.value)}
        style={{ flex: 1, fontSize: 'var(--text-xs, 0.8125rem)' }}
      >
        <option value="">— select named chain —</option>
        {chains.map((c) => (
          <option key={c.id} value={c.id}>
            {c.name}
          </option>
        ))}
      </select>
      <button
        type="button"
        onClick={handleSwitch}
        title="Switch to inline editing (creates a fork — changes won't affect the named chain)"
        style={{ fontSize: 'var(--text-xs, 0.8125rem)', whiteSpace: 'nowrap' }}
      >
        Switch to inline
      </button>
      {onPromoteToCanonical && value && (
        <button
          type="button"
          onClick={onPromoteToCanonical}
          title="Promote this named chain to canonical post-OCR transforms"
          style={{ fontSize: 'var(--text-xs, 0.8125rem)', whiteSpace: 'nowrap' }}
        >
          Promote to canonical
        </button>
      )}
    </div>
  );
}
